import { useCallback } from 'react';

import { PrescriptionHistoryQuery } from '@dreryk/egabinet-prescription-frontend-api';
import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { ContentCopyIcon, IconWrapper, StyledFlex, Tooltip } from '@dreryk/react-components';
import { refetchCqrsQuery } from '@dreryk/command-query-react-common';

import { ContinuouslyUsedMedicineButton } from '~libs.desktop.shared.medicine';
import {
   getSelectablePrescriptionIds,
   PatientPrescriptionHistoryTable,
   PrescriptionHistoryRenderAccordionRowActions,
   PrescriptionHistoryRenderOptionsCell,
} from '~libs.desktop.shared.patient-prescription-history';

import { prescriptionsTranslationKeys } from '../../../translations';
import { CopyPrescriptionPackageAction } from '../../../visitSidePanel/historicalVisits/actions/CopyPrescriptionPackageAction';
import { CopyPrescriptionAction } from '../../../visitSidePanel/historicalVisits/actions/CopyPrescriptionAction';

import { PatientPrescriptionHistoryPanelFilters } from './PatientPrescriptionHistoryPanelFilters';
import { usePatientPrescriptionHistoryPanelFilters } from './PatientPrescriptionHistoryPanelFilters.hooks';
import { StyledPatientPrescriptionHistoryMinWidth } from './PatientPrescriptionHistoryPanel.styled';

type PatientPrescriptionHistoryPanelProps = {
   readonly patientId: number;
   readonly visitId: number;
   readonly visitDiagnoses: VisitDiagnosis[];
};

// eslint-disable-next-line max-lines-per-function
export const PatientPrescriptionHistoryPanel = (props: PatientPrescriptionHistoryPanelProps): JSX.Element => {
   const {
      filters,
      handleIssuedByCurrentPractitionerChange,
      handleContinuouslyUsedMedicineChange,
   } = usePatientPrescriptionHistoryPanelFilters();

   const handleMedicineProcessed = useCallback((): void => {
      refetchCqrsQuery(PrescriptionHistoryQuery);
   }, []);

   const renderOptionsCell = useCallback<PrescriptionHistoryRenderOptionsCell>(
      (prescription, patientMedicine) => {
         const valid = patientMedicine.medicine.valid !== false;
         return (
            <>
               <ContinuouslyUsedMedicineButton
                  medicineId={patientMedicine.medicine.id}
                  patientId={props.patientId}
                  continuouslyUsedMedicineId={patientMedicine.patientContinuouslyUsedMedicineId}
                  medicineName={patientMedicine.medicine.fullName}
                  medicineValid={valid}
                  onMedicineProcessed={handleMedicineProcessed}
               />
               <CopyPrescriptionAction
                  prescriptionId={prescription.prescriptionId}
                  targetVisitId={props.visitId}
                  patientId={props.patientId}
                  visitDiagnoses={props.visitDiagnoses}
               >
                  {({ copyPrescription }) => (
                     <Tooltip
                        content={prescriptionsTranslationKeys.prescriptionHistoryPanel.copyPrescription}
                        testId={'copyPrescription'}
                     >
                        <IconWrapper
                           onClick={async () => {
                              await copyPrescription();
                           }}
                           testId={'copyPrescription'}
                        >
                           <ContentCopyIcon />
                        </IconWrapper>
                     </Tooltip>
                  )}
               </CopyPrescriptionAction>
            </>
         );
      },
      [props.patientId, props.visitId, props.visitDiagnoses, handleMedicineProcessed]
   );

   const renderAccordionRowActions = useCallback<PrescriptionHistoryRenderAccordionRowActions>(
      (prescriptionDailyGroup) => {
         const prescriptionIds = getSelectablePrescriptionIds(prescriptionDailyGroup);
         if (!prescriptionIds.length) {
            return null;
         }
         return (
            <>
               <CopyPrescriptionPackageAction
                  prescriptionIds={prescriptionIds}
                  targetVisitId={props.visitId}
                  patientId={props.patientId}
                  visitDiagnoses={props.visitDiagnoses}
               >
                  {({ copyPrescriptionPackage }) => (
                     <Tooltip
                        content={prescriptionsTranslationKeys.prescriptionHistoryPanel.copyPrescription}
                        testId={'copyPrescriptionPackage'}
                     >
                        <IconWrapper onClick={copyPrescriptionPackage} testId={'copyPrescriptionPackage'}>
                           <ContentCopyIcon />
                        </IconWrapper>
                     </Tooltip>
                  )}
               </CopyPrescriptionPackageAction>
            </>
         );
      },
      [props.visitId, props.patientId, props.visitDiagnoses]
   );

   return (
      <StyledFlex direction={'column'}>
         <PatientPrescriptionHistoryPanelFilters
            filters={filters}
            onIssuedByCurrentPractitionerChange={handleIssuedByCurrentPractitionerChange}
            onContinuouslyUsedMedicineChange={handleContinuouslyUsedMedicineChange}
         />
         <StyledPatientPrescriptionHistoryMinWidth>
            <PatientPrescriptionHistoryTable
               patientId={props.patientId}
               filters={filters}
               renderOptionsCell={renderOptionsCell}
               renderAccordionRowActions={renderAccordionRowActions}
            />
         </StyledPatientPrescriptionHistoryMinWidth>
      </StyledFlex>
   );
};
